/**
 * ========================
 * STORE DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Store domain — the non-vendor merch catalogue and
 * the legacy shop orders and payments that consume it. Vendor-marketplace
 * administration lives in the Vendors domain instead.
 *
 * Sections:
 *  1. Overview  — Dashboard
 *  2. Sales     — Orders, Payments, Customers
 *  3. Catalogue — Products, Low Stock
 *
 * Access: Admin or ShopAdmin, backed by the Store.* rights seeded in migration 236.
 */

import {
  IconLayoutDashboard,
  IconShoppingBag,
  IconCreditCard,
  IconUsers,
  IconPackage,
  IconAlertTriangle,
  IconPlus,
  IconClipboardList,
  IconTableOptions,
  IconArrowsExchange,
  IconScale,
  IconReportAnalytics,
  IconInbox,
  IconLayoutKanban,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { anyVisibilityCheck } from './BaseMenuProvider';
import { useStoreBadgesStore } from '@/domain/badges/storeBadgesStore';

class StoreMenuProvider implements DomainMenuProvider {
  domainId = 'store';

  getMenuConfig() {
    const shopAdmins = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Dashboard.View'] },
    );
    const orderAccess = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Order.View'] },
    );
    const paymentAccess = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Payment.View'] },
    );
    const productAccess = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Product.View'] },
    );
    const stockAccess = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Stock.View'] },
    );
    const orderCreate = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Order.Create'] },
    );
    const productCreate = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Product.Create'] },
    );
    // Lookups reshape the whole domain, so they sit behind the broadest right.
    const lookupAccess = anyVisibilityCheck(
      { roles: ['Admin', 'ShopAdmin'] },
      { permissions: ['Store.Dashboard.View'] },
    );

    const sections: MenuSection[] = [
      // Overview
      {
        id: 'store-overview',
        label: 'Overview',
        items: [
          {
            id: 'store-dashboard',
            label: 'Dashboard',
            path: '/app/store/dashboard',
            icon: <IconLayoutDashboard size={16} />,
            visible: shopAdmins,
          },
        ],
      },

      // Sales
      {
        id: 'store-sales',
        label: 'Sales',
        items: [
          {
            id: 'store-new-orders',
            label: 'New Orders',
            path: '/app/store/orders/queue',
            icon: <IconInbox size={16} />,
            visible: orderAccess,
            // Awaiting Payment + Paid — the orders someone still has to act on.
            countSelector: () => useStoreBadgesStore.getState().newOrders || null,
            badgeColor: 'orange',
          },
          {
            id: 'store-order-board',
            label: 'Order Board',
            path: '/app/store/orders/board',
            icon: <IconLayoutKanban size={16} />,
            visible: orderAccess,
          },
          {
            id: 'store-orders',
            label: 'All Orders',
            path: '/app/store/orders',
            icon: <IconShoppingBag size={16} />,
            visible: orderAccess,
          },
          {
            id: 'store-payments',
            label: 'Payments',
            path: '/app/store/payments',
            icon: <IconCreditCard size={16} />,
            visible: paymentAccess,
          },
          {
            id: 'store-customers',
            label: 'Customers',
            path: '/app/store/customers',
            icon: <IconUsers size={16} />,
            visible: orderAccess,
          },
          {
            id: 'store-reconciliation',
            label: 'Reconciliation',
            path: '/app/store/reconciliation',
            icon: <IconScale size={16} />,
            visible: paymentAccess,
          },
          {
            id: 'store-picking-list',
            label: 'Picking List',
            path: '/app/store/orders/picking-list',
            icon: <IconClipboardList size={16} />,
            visible: orderAccess,
            // Paid but not yet packed — what is actually waiting on this page.
            countSelector: () => useStoreBadgesStore.getState().awaitingFulfilment || null,
            badgeColor: 'blue',
          },
        ],
      },

      // Create — the actions an admin reaches for most often, one click from anywhere
      {
        id: 'store-create',
        label: 'Create',
        items: [
          {
            id: 'store-new-order',
            label: 'New Order',
            path: '/app/store/orders/new',
            icon: <IconPlus size={16} />,
            visible: orderCreate,
          },
          {
            id: 'store-new-product',
            label: 'New Product',
            path: '/app/store/products/new',
            icon: <IconPlus size={16} />,
            visible: productCreate,
          },
        ],
      },

      // Catalogue
      {
        id: 'store-catalogue',
        label: 'Catalogue',
        items: [
          {
            id: 'store-products',
            label: 'Products',
            path: '/app/store/products',
            icon: <IconPackage size={16} />,
            visible: productAccess,
          },
          {
            id: 'store-low-stock',
            label: 'Low Stock',
            path: '/app/store/products/low-stock',
            icon: <IconAlertTriangle size={16} />,
            visible: stockAccess,
            // Out of stock is red because it is already costing sales; merely low is not.
            countSelector: () => useStoreBadgesStore.getState().lowStock || null,
            badgeColor: 'red',
          },
          {
            id: 'store-stock-movements',
            label: 'Stock Movements',
            path: '/app/store/products/stock-movements',
            icon: <IconArrowsExchange size={16} />,
            visible: stockAccess,
          },
        ],
      },

      // Reports
      {
        id: 'store-reports',
        label: 'Reports',
        items: [
          {
            id: 'store-reports-all',
            label: 'All Reports',
            path: '/app/store/reports',
            icon: <IconReportAnalytics size={16} />,
            visible: shopAdmins,
          },
        ],
      },

      // Administration
      {
        id: 'store-admin',
        label: 'Administration',
        collapsible: true,
        defaultOpen: false,
        items: [
          {
            id: 'store-lookups',
            label: 'Lookups',
            path: '/app/store/lookups',
            icon: <IconTableOptions size={16} />,
            visible: lookupAccess,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'Store management menu - sectioned layout',
      },
    };
  }
}

export const storeMenuProvider = new StoreMenuProvider();
